import { useAuthContext } from "@auth/context/Auth.context";
import {
  ClbTab,
  ClbTabContent,
  ClbTabGroup,
  useToast,
} from "@celebration/react";
import { Drawer } from "@components/index";
import {
  Button,
  Conditional,
  Flex,
  Icons,
  TimePicker,
} from "@components/index";
import EmptyFragment from "@fragments/Empty.fragment";
import { useProcessExecution } from "@services/processExecution/hooks";
import StatusGanttFragment from "@src/common/fragments/StatusGantt.fragment";
import { useChannelIcons } from "@src/common/hooks";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { formatDateUTCToTimeLocal } from "@utils/datetime";
import { useCallback, useMemo, useState } from "react";
import { useDetailsGanttContext } from "../../contexts/DetailsGantt.context";
import { useFilterContext } from "../../contexts/Filter.context";
import NonExecutionButton from "../Details/fragments/NonExecution";
import styles from "./DetailsGantt.module.scss";
import ExecutionEventLogLine from "./ExecutionEventLogLine.component";
import Justification from "./fragments/Justification/Justification.fragment";
import { NegExecutionFragment } from "./fragments/NegExecution";

enum DetailsGanttTab {
  Geral = 1,
  Detalhamento,
  Log,
}

const toUtcWithToday = (time: Date | null, baseDate: Date): string | null => {
  if (!time || Number.isNaN(time.getTime())) return null;
  const combined = new Date(baseDate);
  combined.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
  return combined.toISOString();
};


// Searches the React Query cache for an event by ID using exact date keys,
// avoiding stale caches from other dates that may contain outdated executionEvents.
const findEventInCache = (
  queryClient: QueryClient,
  eventId: number | undefined,
  filterDate: Date
): Sentinel.Timeline.Event | undefined => {
  if (!eventId) return undefined;

  // Gantt cache (detalhada view)
  const ganttData = queryClient.getQueryData<Sentinel.Timeline.GanttResponse>([
    "timeline",
    "gantt",
    filterDate,
  ]);
  for (const group of ganttData?.groupedEvents ?? []) {
    const found = group.events?.find((e) => e.id === eventId);
    if (found) return found;
  }

  // Management cache (gerencial view)
  const managementData = queryClient.getQueryData<Sentinel.Timeline.New>([
    "timeline",
    filterDate,
  ]);
  for (const lane of managementData?.lanedEvents ?? []) {
    for (const group of lane.groupedEvents ?? []) {
      const found = group.events?.find((e) => e.id === eventId);
      if (found) return found;
    }
  }

  return undefined;
};

function DetailsGantt() {
  const { event, open, onClose, onEventUpdate } = useDetailsGanttContext();
  const { date: filterDate } = useFilterContext();
  const { hasSomeResourceRole } = useAuthContext();
  const canEdit = hasSomeResourceRole("admin", "timeline-manager");
  const canRegisterJustification = hasSomeResourceRole(
    "admin",
    "timeline-manager"
  );
  const { getChannelIconWithName } = useChannelIcons(event);

  const [activeTab, setActiveTab] = useState<DetailsGanttTab>(
    DetailsGanttTab.Geral
  );

  // Verifica se é um processo NEG D1 para exibir a aba Detalhamento
  const isNegProcess = useMemo(() => {
    const name = event?.name?.toLowerCase() ?? "";
    const displayName = event?.displayName?.toLowerCase() ?? "";
    return name.includes("neg") || displayName.includes("neg");
  }, [event?.name, event?.displayName]);
  const isAwaitingJustification =
    (event?.status === "COMPLETED_LATE_AWAIT_JUSTIFICATION" ||
      event?.status === "COMPLETED_LATE_AWAIT_FINALIZE_JUSTIFICATION") &&
    event?.hasCompleteJustification !== true;
  const canConsultJustification = event?.hasCompleteJustification === true;
  const [showEditTime, setShowEditTime] = useState(false);
  const [showJustificationDrawer, setShowJustificationDrawer] = useState(false);
  const [editStart, setEditStart] = useState<Date | null>(null);
  const [editEnd, setEditEnd] = useState<Date | null>(null);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { manualChange } = useProcessExecution();

  const formatDate = useMemo(
    () => (date: string) => formatDateUTCToTimeLocal(date, "--:--"),
    []
  );

  const handleOpenEdit = useCallback(() => {
    setEditStart(event?.executionStart ? new Date(event.executionStart) : null);
    setEditEnd(event?.executionEnd ? new Date(event.executionEnd) : null);
    setShowEditTime(true);
  }, [event?.executionStart, event?.executionEnd]);

  const handleCancelEdit = useCallback(() => {
    setShowEditTime(false);
  }, []);

  const handleOpenJustificationDrawer = useCallback(() => {
    setShowJustificationDrawer(true);
  }, []);


  const handleCloseJustificationDrawer = useCallback(() => {
    setShowJustificationDrawer(false);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setActiveTab(DetailsGanttTab.Geral);
    setShowEditTime(false);
    handleCloseJustificationDrawer();
    onClose();
  }, [handleCloseJustificationDrawer, onClose]);

  const isEditValid = useMemo(() => {
    // A manual launch always needs a valid start. The end is optional, but
    // must be valid whenever the user informs it.
    const startOk = !!editStart && !Number.isNaN(editStart.getTime());
    const endOk = !editEnd || !Number.isNaN(editEnd.getTime());
    return startOk && endOk;
  }, [editStart, editEnd]);

  const handleSaveEdit = useCallback(async () => {
    if (!event?.id) return;
    try {
      const baseDate = filterDate ?? new Date();
      await manualChange(event.id, {
        newExecutionStartInUtc: toUtcWithToday(editStart, baseDate),
        newExecutionEndInUtc: toUtcWithToday(editEnd, baseDate),
      });
      toast.openToast({
        title: "Sucesso",
        description: "Horário real atualizado com sucesso!",
        type: "positive",
        autoClose: true,
      });

      // Immediately update times in context so the Geral tab reflects the change
      const newStart = toUtcWithToday(editStart, baseDate);
      const newEnd = toUtcWithToday(editEnd, baseDate);
      onEventUpdate?.({
        ...(newStart !== null && { executionStart: newStart }),
        ...(newEnd !== null && { executionEnd: newEnd }),
      });

      // Cancel any in-flight timeline requests before refetching, to avoid
      // duplicate calls with stale dates (e.g., polling interval firing simultaneously)
      await queryClient.cancelQueries({ queryKey: ["timeline"] });
      // Refetch exactly the queries for the current filter date
      await queryClient.refetchQueries({
        queryKey: ["timeline", filterDate],
        exact: true,
      });
      await queryClient.refetchQueries({
        queryKey: ["timeline", "gantt", filterDate],
        exact: true,
      });
      void queryClient.invalidateQueries({ queryKey: ["neg-execution"] });

      // Update executionEvents from fresh cache only if the response includes them —
      // timeline endpoints may not return executionEvents, in which case we preserve the existing logs
      const updatedEvent = findEventInCache(
        queryClient,
        event?.id,
        filterDate ?? new Date()
      );
      if (updatedEvent?.executionEvents?.length) {
        onEventUpdate?.({ executionEvents: updatedEvent.executionEvents });
      }
      setShowEditTime(false);
    } catch {
      toast.openToast({
        title: "Erro",
        description: "Erro ao atualizar o horário.",
        type: "negative",
        autoClose: true,
      });
    }
  }, [
    event?.id,
    editStart,
    editEnd,
    filterDate,
    manualChange,
    toast,
    queryClient,
    onEventUpdate,
  ]);

  return (
    <>
      <Drawer
        overlay
        title={event?.displayName}
        isOpen={open}
        onClose={onCloseDrawer}
        idUsedToOpenDrawer="open-drawer-gantt"
      >
        <div className="pb-10">
          <StatusGanttFragment event={event} size="large" />
        </div>

        <ClbTabGroup activeTabNumber={activeTab} onTabChanged={setActiveTab}>
          <ClbTab tabNumber={DetailsGanttTab.Geral} title="Geral" />
          {isNegProcess && (
            <ClbTab
              tabNumber={DetailsGanttTab.Detalhamento}
              title="Detalhamento"
            />
          )}
          <ClbTab tabNumber={DetailsGanttTab.Log} title="Log" />

          <ClbTabContent tabNumber={DetailsGanttTab.Geral}>
            <Flex.Col gap="15px" className="py-10">
              <Flex.Row>
                <div className={styles["sub-title"]}>Horários</div>
              </Flex.Row>
              <Flex.Col gap="2px">
                <Flex.Row gap="4px">
                  <div className={styles["sub-title-label"]}>Previsto:</div>
                  <div className={styles["sub-title-date"]}>
                    {formatDate(event?.plannedExecutionStart)} -{" "}
                    {formatDate(event?.plannedExecutionEnd)}
                  </div>
                </Flex.Row>
                <Conditional condition={!showEditTime}>
                  <Flex.Row gap="4px" className={styles["real-row"]}>
                    <div className={styles["sub-title-label"]}>Real:</div>
                    <div className={styles["sub-title-date"]}>
                      {formatDate(event?.executionStart)} -{" "}
                      {formatDate(event?.executionEnd)}
                    </div>
                    <Conditional
                      condition={
                        canEdit &&
                        !!event?.id
                      }
                    >
                      <Icons
                        icon="edit"
                        size="sm"
                        className={styles["edit-icon-btn"]}
                        onClick={handleOpenEdit}
                      />
                    </Conditional>
                  </Flex.Row>
                </Conditional>

                <Conditional condition={showEditTime}>
                  <Flex.Col gap="8px" className={styles["edit-time-container"]}>
                    <div
                      className={`${styles["sub-title-label"]} ${styles["edit-time-label"]}`}
                    >
                      Horário real de execução
                    </div>
                    <Flex.Row gap="8px">
                      <TimePicker
                        id="edit-start"
                        format="HH:mm"
                        value={editStart}
                        onChange={setEditStart}
                        placement="autoVerticalStart"
                        cleanable={false}
                      />
                      <TimePicker
                        id="edit-end"
                        format="HH:mm"
                        value={editEnd}
                        onChange={setEditEnd}
                        placement="autoVerticalStart"
                        cleanable={false}
                      />
                    </Flex.Row>
                    <Flex.Row gap="8px" justifyContent="end">
                      <Button
                        label="Cancelar"
                        buttonStyle="secondary"
                        size="sm"
                        onClick={handleCancelEdit}
                      />
                      <Button
                        label="Salvar"
                        buttonStyle="primary"
                        size="sm"
                        disabled={!isEditValid}
                        onClick={() => void handleSaveEdit()}
                      />
                    </Flex.Row>
                  </Flex.Col>
                </Conditional>
              </Flex.Col>

              <div className={styles.divider} />

              <Flex.Row>
                <div className={styles["sub-title"]}>Canal</div>
              </Flex.Row>
              <Flex.Col gap="2px">{getChannelIconWithName()}</Flex.Col>

              <div className={styles.divider} />

              <Flex.Row>
                <div className={styles["sub-title"]}>Informações</div>
              </Flex.Row>
              <Flex.Col gap="4px">
                <div className={styles["sub-title-label"]}>Descrição</div>
                <div className={styles["sub-title-date"]}>
                  {event?.description ?? "Descrição não informada"}
                </div>
              </Flex.Col>
              <Flex.Col gap="4px">
                <div className={styles["sub-title-label"]}>Link Padrão</div>
                {event?.linkDocumentation ? (
                  <a
                    href={event.linkDocumentation}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles["sub-title-date"]}
                  >
                    {event.linkDocumentation}
                  </a>
                ) : (
                  <div className={styles["sub-title-date"]}>
                    Sem link cadastrado
                  </div>
                )}
              </Flex.Col>
              <div className={styles.divider} />

              {(isAwaitingJustification || canConsultJustification) &&
                canRegisterJustification && (
                  <button
                    type="button"
                    className={`${styles["justification-link"]} ${canConsultJustification ? styles["justification-link-readonly"] : ""}`}
                    onClick={handleOpenJustificationDrawer}
                  >
                    <Icons icon="info" size="sm" />
                    <span>
                      {canConsultJustification
                        ? "Deseja consultar justificativa?"
                        : "Deseja adicionar uma justificativa ao processo?"}
                    </span>
                  </button>
                )}

              {/* Botão de Não Execução */}
              <NonExecutionButton event={event} onSuccess={onCloseDrawer} />
            </Flex.Col>
          </ClbTabContent>

          {isNegProcess && (
            <ClbTabContent tabNumber={DetailsGanttTab.Detalhamento}>
              <NegExecutionFragment
                enabled={activeTab === DetailsGanttTab.Detalhamento}
              />
            </ClbTabContent>
          )}

          <ClbTabContent tabNumber={DetailsGanttTab.Log}>
            {event?.executionEvents?.length ? (
              <Flex.Col gap="15px" className="px-10 py-10">
                {event.executionEvents.map((event, index) => (
                  <ExecutionEventLogLine
                    key={`${event.eventType}-${index}`}
                    event={event}
                  />
                ))}
              </Flex.Col>
            ) : (
              <EmptyFragment show size="sm" className="pt-50">
                Sem dados
              </EmptyFragment>
            )}
          </ClbTabContent>
        </ClbTabGroup>
      </Drawer>

      {event && (
        <Justification
          event={event}
          filterDate={filterDate}
          isOpen={
            showJustificationDrawer &&
            (isAwaitingJustification || canConsultJustification)
          }
          onClose={handleCloseJustificationDrawer}
        />
      )}

    </>
  );
}

export default DetailsGantt;
